import { FinalTourPrice } from "@/components/site/FinalTourPrice";
import { GeneralCancellationSummary } from "@/components/tour/GeneralCancellationSummary";
import { priceUnitLabel } from "@/lib/catalog-presentation";
import { WhatsApp24 } from "@/components/site/WhatsApp24";
import { BuenFinBadge } from "@/components/site/BuenFinCampaign";
// Plantilla V2 de la página individual de tour (detrás del interruptor
// `resolveTourV2`). No sustituye nada: el diseño actual sigue intacto y es el
// predeterminado en producción.
//
// Todo el contenido proviene de las fuentes dinámicas existentes: producto
// unificado (Bókun / tour propio), disponibilidad y precios reales, galería
// real, y el sistema seguro de Maya para la guía completa.
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  Bird,
  Bus,
  CalendarDays,
  Camera,
  ChevronLeft,
  Clock,
  Droplets,
  Fish,
  Landmark,
  Leaf,
  MapPin,
  Ship,
  Sparkles,
  Sun,
  UserCheck,
  UtensilsCrossed,
  Waves,
} from "lucide-react";

import { ProductGalleryLazy as ProductGallery } from "@/components/product/ProductGalleryLazy";
import { ShareButton } from "@/components/site/ShareButton";
import { DestinationDiscovery } from "@/components/tour/DestinationDiscovery";
import { FreeDestinationGuideDownload } from "@/components/tour/FreeDestinationGuideDownload";
import { MayaAudioGuide } from "@/components/maya/MayaAudioGuide";
import { MAYA_PREMIUM_ENABLED } from "@/lib/maya-feature-flag";
import { ReviewsSection } from "@/components/tour/ReviewsSection";
import { CancellationPolicyBlock } from "@/components/tour/CancellationPolicyBlock";
import { DirectGuideBenefit } from "@/components/site/DirectGuideBenefit";
import { TourV2BookingCard } from "@/components/tour/v2/TourV2BookingCard";
import { TourPreviewDateSelector } from "@/components/tour/v2/TourPreviewDateSelector";
import { TourV2BookingFlow } from "@/components/tour/v2/TourV2BookingFlow";
import {
  emptySelection,
  type TourV2Quote,
  type TourV2Selection,
} from "@/components/tour/v2/tour-v2-selection";
import { TourV2MayaPanel } from "@/components/tour/v2/TourV2MayaPanel";
import { TourV2Related } from "@/components/tour/v2/TourV2Related";
import { TourV2Sections } from "@/components/tour/v2/TourV2Sections";
import { getRecentTour, saveRecentTour } from "@/lib/recent-tour";
import { trackTourV2 } from "@/lib/tour-v2-analytics";
import { deriveTourFacts, type FeatureIcon } from "@/lib/tour-v2-facts";
import { CustomTourBookingPanel } from "@/components/tour/CustomTourBookingPanel";
import { formatDuration } from "@/lib/slug";
import { useFormatPrice } from "@/hooks/use-format-price";
import { useCurrency, useMoney } from "@/lib/currency";
import { cn } from "@/lib/utils";
import { resolveBookingProductId, NOT_CONFIGURED_IN_TEST } from "@/lib/bokun-product-map";
import { MayaSandboxBooking } from "@/components/maya/sales/MayaSandboxBooking";
import { TOUR_V2_OWN_CHECKOUT } from "@/lib/tour-v2-flag";
import type { V2BarState } from "@/components/maya/sales/MayaSandboxBooking";
import { getLiveCheckoutAccess } from "@/lib/bokun-live-booking.functions";
import { useAuth } from "@/hooks/use-auth";
import { BokunOfficialWidget } from "@/components/booking/BokunOfficialWidget";
import { TourV2HelpBox } from "@/components/tour/v2/TourV2HelpBox";
import { Pyramid } from "lucide-react";
import { pricesHeadingKey } from "@/lib/tour-v2-facts";
import { detectTourPlaces } from "@/lib/tour-places";
import { TourV2OfficialCard, useOfficialPrice, officialFrom } from "@/components/tour/v2/TourV2OfficialCard";

const TEST_ONLY_PRODUCT_IDS = new Set(["28150", "28141"]);
import type { UnifiedTourDetail } from "@/lib/catalog.functions";

const ICONS: Record<FeatureIcon, typeof Waves> = {
  waves: Waves,
  ship: Ship,
  leaf: Leaf,
  fish: Fish,
  landmark: Landmark,
  food: UtensilsCrossed,
  droplets: Droplets,
  bird: Bird,
  bus: Bus,
  guide: UserCheck,
  camera: Camera,
  sun: Sun,
  pyramid: Pyramid,
};

type RightPanel = "booking" | "maya";

export function TourV2Layout({
  tour: p,
  slug,
  lang,
  summary,
  desc,
  noindex,
  previewDateSelector = false,
}: {
  tour: UnifiedTourDetail;
  slug: string;
  lang: string;
  summary: string;
  desc: string;
  noindex?: boolean;
  previewDateSelector?: boolean;
}) {
  useCurrency(); // subscribe: formatPrice reads the currency store
  const { t } = useTranslation();
  const formatPrice = useFormatPrice();
  const auth = useAuth();
  const [panel, setPanel] = useState<RightPanel>("booking");
  // Una sola reservación continua: selección → datos → pago, en un contenedor.
  const [flowOpen, setFlowOpen] = useState(false);
  const [flowStep, setFlowStep] = useState<1 | 2 | 3>(1);
  const [selection, setSelection] = useState<TourV2Selection>(emptySelection);
  const [quote, setQuote] = useState<TourV2Quote | null>(null);

  const dur = p.durationText ?? formatDuration(p.durationMinutes);
  const isBokun = p.source === "bokun";
  const facts = useMemo(
    () =>
      deriveTourFacts({
        title: p.title,
        location: p.location,
        summary: p.summary,
        description: p.description,
        categories: p.categories,
        id: p.source === "bokun" ? p.id : null,
        highlights: p.highlights,
        included: p.included,
        durationMinutes: p.durationMinutes,
      }),
    [p],
  );

  const activityType = p.bookingType
    ? t(`tourV2.activityTypes.${p.bookingType}`, {
        defaultValue: facts.fullDay ? "Tour de día" : "Actividad",
      })
    : facts.fullDay
      ? t("tourV2.dayTour", { defaultValue: "Tour de día" })
      : t("tourV2.activity", { defaultValue: "Actividad" });

  const metaLine = [p.location, dur, activityType].filter(Boolean).join(" · ");
  // Destino del tour (lo que visita), nunca la ciudad de salida de Bókun.
  const visitedPlace = detectTourPlaces({ title: p.title }).places[0]?.name ?? null;
  const emotional = visitedPlace
    ? t("tourV2.escapeTo", { place: visitedPlace, defaultValue: `Escápate a ${visitedPlace}` })
    : p.title;
  const phrase = facts.fullDay
    ? t("tourV2.phraseDay", { defaultValue: "Un día que recordarás siempre" })
    : t("tourV2.phraseShort", { defaultValue: "Una experiencia que recordarás siempre" });

  const priceLabel = p.priceFrom != null ? formatPrice(p.priceFrom, p.currency ?? "MXN") : null;

  // `?v2=1` no debe indexarse como página distinta; el canonical limpio sigue
  // intacto y aquí solo añadimos la señal de no indexación.
  useEffect(() => {
    if (!noindex || typeof document === "undefined") return;
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    tag.setAttribute("data-tour-v2", "1");
    document.head.appendChild(tag);
    return () => {
      tag.remove();
    };
  }, [noindex]);

  const openFlow = (step: 1 | 2 | 3 = 1) => {
    // Se invoca en eventos, después del render: `officialMode` ya existe.
    if (officialMode) {
      goToOfficialCalendar();
    } else {
      setFlowStep(step);
      setFlowOpen(true);
    }
    trackTourV2("booking_started", {
      tourSlug: slug,
      pax: quote?.pax,
      amount: quote?.total,
      currency: quote?.currency,
    });
  };

  // Recuperación de interés: guarda el tour visto (sin datos personales) tras
  // 10 segundos en la página y registra la visita.
  useEffect(() => {
    const id = window.setTimeout(() => {
      trackTourV2("tour_viewed", { tourSlug: slug, destination: p.location });
      saveRecentTour({
        id: p.id,
        slug,
        title: p.title,
        destination: p.location,
        image: p.images[0] ?? null,
        priceFrom: p.priceFrom,
        currency: p.currency ?? "MXN",
      });
    }, 10_000);
    return () => window.clearTimeout(id);
  }, [slug, p.id, p.title, p.location, p.images, p.priceFrom, p.currency]);

  // Borrador de la selección: se guarda localmente y se vuelve a validar
  // siempre contra la API antes de presentarlo como disponible.
  useEffect(() => {
    if (!selection.date && Object.keys(selection.qty).length === 0) return;
    saveRecentTour({
      id: p.id,
      slug,
      title: p.title,
      destination: p.location,
      image: p.images[0] ?? null,
      priceFrom: p.priceFrom,
      currency: p.currency ?? "MXN",
      draft: {
        date: selection.date,
        startTime: selection.startTime,
        startTimeId: selection.startTimeId,
        qty: selection.qty,
      },
    });
    if (selection.date) trackTourV2("date_selected", { tourSlug: slug, date: selection.date });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  // Al regresar desde "Continúa donde te quedaste" recuperamos el borrador.
  const [resumed, setResumed] = useState(false);
  useEffect(() => {
    if (resumed) return;
    setResumed(true);
    const recent = getRecentTour();
    if (recent?.slug !== slug || !recent?.draft?.date) return;
    setSelection((prev) => ({
      ...prev,
      date: recent.draft?.date ?? null,
      startTime: recent.draft?.startTime ?? null,
      startTimeId: recent.draft?.startTimeId ?? null,
      qty: recent.draft?.qty ?? {},
    }));
  }, [resumed, slug]);

  /** Lleva al calendario oficial integrado (visible en la página, sin ventanas). */
  const [calendarOpen, setCalendarOpen] = useState(false);
  const goToOfficialCalendar = () => {
    if (typeof document === "undefined") return;
    setPanel("booking");
    setCalendarOpen(true);
    window.setTimeout(() => {
      document.getElementById("v2-calendario")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  /** Lleva al único calendario propio (columna derecha en escritorio, bloque en móvil). */
  const goToCalendar = () => {
    if (typeof document === "undefined") return;
    const mobile = document.getElementById("v2-reservar");
    const target = mobile && mobile.offsetParent !== null ? mobile : null;
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('calendar') !== '1') return;
    const timer = window.setTimeout(() => {
      const opener = document.querySelector<HTMLButtonElement>('[data-testid="v2-open-calendar"]');
      if (opener) opener.click();
      document.getElementById('v2-reservar')?.scrollIntoView({ block: 'start' });
    }, 300);
    return () => window.clearTimeout(timer);
  }, []);

  // `hideCta` en móvil: allí el único botón principal es la barra fija.
  // Modo Test: la reserva usa SOLO el producto de Bókun Test (categorías, IDs,
  // tarifas, horarios y preguntas propios) mediante el mismo servicio que Maya.
  const testProductId = TOUR_V2_OWN_CHECKOUT ? resolveBookingProductId(slug, "sandbox") : null;
  // Producción: checkout propio con el ID real del producto (nunca de prueba);
  // el servidor decide quién puede usarlo (BOKUN_LIVE_CHECKOUT_MODE).
  const liveProductId =
    !TOUR_V2_OWN_CHECKOUT && isBokun && p.bokunActivityId && !TEST_ONLY_PRODUCT_IDS.has(String(p.bokunActivityId))
      ? String(p.bokunActivityId)
      : null;
  const fetchLiveAccess = useServerFn(getLiveCheckoutAccess);
  const { data: liveAccess } = useQuery({
    queryKey: ["live-checkout-access", auth.user?.id ?? "anon"],
    queryFn: () => fetchLiveAccess(),
    enabled: !!liveProductId && auth.ready,
    staleTime: 5 * 60_000,
  });
  // Arquitectura definitiva: sin checkout propio, Bókun oficial controla
  // calendario, categorías, precios y total. `liveAccess` ya no habilita nada.
  const liveEnabled = TOUR_V2_OWN_CHECKOUT && !!liveProductId && !!liveAccess?.allowed;
  const officialMode = !TOUR_V2_OWN_CHECKOUT && !!liveProductId;
  // Selector propio (fecha/pasajeros → apartado → pago) en TODOS los tours
  // Bókun cuando el servidor permite el checkout a este visitante
  // (BOKUN_LIVE_CHECKOUT_MODE). Si no, se queda el calendario oficial.
  const selectorOn = previewDateSelector || !!liveProductId;
  // El calendario oficial se monta una sola vez: columna derecha (escritorio)
  // o bajo el hero (móvil). Nunca dos widgets de Bókun a la vez.
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const on = () => setIsDesktop(mql.matches);
    on();
    mql.addEventListener("change", on);
    return () => mql.removeEventListener("change", on);
  }, []);
  // Checkout propio: la barra móvil refleja exactamente la selección de la tarjeta.
  const [ownBar, setOwnBar] = useState<V2BarState | null>(null);
  const ownOpenRef = useRef<(() => void) | null>(null);
  const money = useMoney();
  const { currency: shownCurrency } = useCurrency();
  // Precio real que cobra la ventana oficial (puede diferir de la tarifa base).
  const { data: officialPrice } = useOfficialPrice(officialMode ? liveProductId : null, p.pricingCategories ?? []);
  const officialFromAmount = officialFrom(officialPrice?.categoryPrices, (p.pricingCategories ?? []) as never);
  const shownPriceLabel = officialMode
    ? officialFromAmount != null
      ? `${money(officialFromAmount, officialPrice?.currency ?? "MXN", 2)} / ${t("tourV2.perAdult", { defaultValue: "adulto" })}`
      : null
    : priceLabel;
  const barStarted = !!quote && (!!quote.selection.date || quote.pax > 0);
  const renderBookingCard = (hideCta = false) =>
    selectorOn && isBokun ? (
      <TourPreviewDateSelector tour={p} active desktop={isDesktop === true} />
    ) : isBokun && testProductId ? (
      <MayaSandboxBooking testProductId={testProductId} slug={slug} lang={lang} testOnly={false} source="v2" onBarChange={hideCta ? setOwnBar : undefined} openRef={hideCta ? ownOpenRef : undefined} />
    ) : liveEnabled && liveProductId ? (
      <MayaSandboxBooking testProductId={liveProductId} slug={slug} lang={lang} testOnly={false} source="v2" env="live" onBarChange={hideCta ? setOwnBar : undefined} openRef={hideCta ? ownOpenRef : undefined} />
    ) : officialMode && liveProductId ? (
      <div id={isDesktop === !hideCta ? "v2-calendario" : undefined} className="scroll-mt-24">
        {calendarOpen && isDesktop === !hideCta ? (
          <div className="rounded-2xl border border-border bg-card p-3 shadow-[var(--shadow-elevated)] sm:p-4" data-testid="v2-calendar-panel">
            <button
              type="button"
              onClick={() => setCalendarOpen(false)}
              className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm font-semibold transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
              {t("tourV2.backToPrices", { defaultValue: "Volver a precios" })}
            </button>
            <BokunOfficialWidget productId={liveProductId} lang={lang} currency={shownCurrency} />
          </div>
        ) : (
          <div className="space-y-3">
            <TourV2OfficialCard activityId={liveProductId} categories={p.pricingCategories ?? []} lang={lang} hideCta headingKey={pricesHeadingKey(p.source === "bokun" ? p.id : null)} />
            <button
              type="button"
              onClick={() => {
                setCalendarOpen(true);
                trackTourV2("availability_opened", { tourSlug: slug });
              }}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-base font-bold text-primary-foreground transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              data-testid="v2-open-calendar"
            >
              <CalendarDays className="h-5 w-5" aria-hidden />
              {t("tourV2.seeDatesBook", { defaultValue: "Ver fechas y reservar" })}
            </button>
            <TourV2HelpBox lang={lang} tourTitle={p.title} share={{ slug, priceFrom: p.priceFrom ?? null, currency: p.currency ?? null, image: p.images?.[0] ?? null }} onMaya={MAYA_PREMIUM_ENABLED ? () => setPanel("maya") : undefined} />
          </div>
        )}
      </div>
    ) : isBokun && p.bokunActivityId ? (
      <TourV2BookingCard
        activityId={p.bokunActivityId}
        priceFrom={p.priceFrom}
        currency={p.currency ?? "MXN"}
        pricingCategories={p.pricingCategories}
        minBookable={p.minBookable}
        maxBookable={p.maxBookable}
        selection={selection}
        onSelectionChange={setSelection}
        onQuoteChange={setQuote}
        onContinue={() => openFlow(1)}
        hideCta={hideCta}
      />
    ) : (
      <CustomTourBookingPanel
        tourId={p.id.replace(/^mx-/, "")}
        fallbackPriceFrom={p.priceFrom ?? null}
        fallbackCurrency={p.currency ?? "MXN"}
        extras={p.bookingExtras ?? []}
        questions={p.bookingQuestions ?? []}
        meetingPoints={p.meetingPoints ?? []}
        minParticipants={p.minBookable ?? null}
        maxParticipants={p.maxBookable ?? null}
        bookingCutoffHours={p.bookingCutoffHours ?? null}
        requiresHotelInfo={!!p.requiresHotelInfo}
        requiresFlightInfo={!!p.requiresFlightInfo}
        hasTerms={!!p.terms}
      />
    );

  const mayaPanel = (
    <TourV2MayaPanel
      destinationKey={(p.location || p.title).toLowerCase().trim()}
      destinationName={p.location || p.title}
      location={p.location}
      summary={summary || (desc ? desc.slice(0, 240) : null)}
      lang={lang}
      onClose={() => setPanel("booking")}
    />
  );

  const features = facts.features.map((f) => {
    const Icon = ICONS[f.icon];
    return (
      <span
        key={f.key}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-white/90 drop-shadow"
      >
        <Icon className="h-3.5 w-3.5 text-primary" aria-hidden />
        {t(`tourV2.features.${f.key}`, { defaultValue: f.key })}
      </span>
    );
  });

  return (
    <div className="tour-typography">
      <div className="mx-auto max-w-7xl px-4 pt-3 sm:px-6 lg:px-8">
        <div className="mb-3 flex items-center justify-between gap-3 text-sm">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("nav.backHome", "Volver al inicio")}
          </Link>
          <ShareButton url={`/tour/${slug}`} title={p.title} text={summary || desc.slice(0, 120)} />
        </div>

        {/* Información superior: H1 real + metadatos dinámicos */}
        <header className="mb-4">
          <h1 className="tour-display text-[1.75rem] leading-[1.1] sm:text-[2.25rem] lg:text-[2.5rem]">
            {p.title}
          </h1>
          <BuenFinBadge id={p.id} />
          {metaLine && (
            <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              {p.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" aria-hidden /> {p.location}
                </span>
              )}
              {dur && (
                <>
                  <span aria-hidden>·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-4 w-4" aria-hidden /> {dur}
                  </span>
                </>
              )}
              <span aria-hidden>·</span>
              <span>{activityType}</span>
            </p>
          )}
        </header>

        {/* ====================== HERO ====================== */}
        <div className="grid items-start gap-6 lg:grid-cols-[64fr_36fr]">
          {/* Columna visual */}
          <div className="min-w-0">
            <div className="relative overflow-hidden rounded-3xl">
              <ProductGallery images={p.images} alt={p.title} singleViewAll />
              {/* Degradado mínimo, solo sobre la foto principal y por encima
                  de los propios controles del carrusel. */}
              <div className="pointer-events-none absolute inset-x-0 top-0 hidden aspect-[4/3] flex-col justify-end bg-gradient-to-t from-black/70 via-black/20 to-transparent p-5 pb-16 sm:flex">
                <p className="tour-display text-2xl text-white drop-shadow lg:text-3xl">
                  {emotional}
                </p>
                <p className="mt-1 text-sm font-semibold text-amber-100">{phrase}</p>
                {facts.shortDesc && (
                  <p className="mt-1 max-w-xl text-sm text-white/90">{facts.shortDesc}</p>
                )}
                {features.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{features}</div>}
              </div>
            </div>

            {/* Textos en móvil (fuera de la foto, máxima legibilidad) */}
            <div className="mt-4 sm:hidden">
              <p className="tour-display text-xl">{emotional}</p>
              <p className="mt-1 text-sm font-semibold text-primary">{phrase}</p>
              {facts.shortDesc && (
                <p className="mt-1 text-sm text-foreground/80">{facts.shortDesc}</p>
              )}
              {features.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {facts.features.map((f) => {
                    const Icon = ICONS[f.icon];
                    return (
                      <span
                        key={f.key}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
                      >
                        <Icon className="h-3.5 w-3.5 text-primary" aria-hidden />
                        {t(`tourV2.features.${f.key}`, { defaultValue: f.key })}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Tres accesos sencillos */}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setPanel("booking");
                  document
                    .getElementById("resumen")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Camera className="h-4 w-4" aria-hidden />
                {t("tourV2.seeExperience", { defaultValue: "Ver experiencia" })}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPanel("booking");
                  if (officialMode) openFlow(1);
                  else goToCalendar();
                  trackTourV2("availability_opened", { tourSlug: slug });
                }}
                // En móvil el único botón principal es la barra fija inferior;
                // con el checkout oficial, el único botón es el de la tarjeta.
                className={cn(officialMode || selectorOn ? "hidden" : "hidden lg:inline-flex", "items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2")}
              >
                <Clock className="h-4 w-4" aria-hidden />
                {officialMode
                  ? t("tourV2.officialBook", { defaultValue: "Ver disponibilidad y reservar" })
                  : t("tourV2.pickDate", { defaultValue: "Elegir fecha" })}
              </button>
              {MAYA_PREMIUM_ENABLED && (
                <button
                  type="button"
                  onClick={() => setPanel("maya")}
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 px-4 py-2 text-sm font-semibold text-primary transition hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Sparkles className="h-4 w-4" aria-hidden />
                  {t("tourV2.meetMaya", { defaultValue: "Conocer a Maya" })}
                </button>
              )}
            </div>
          </div>

          {/* One responsive reservation surface, same state across viewport changes. */}
          <aside id="v2-reservar" className="min-w-0 self-stretch scroll-mt-24 lg:col-start-2 lg:row-span-3 lg:row-start-1">
            <div className={calendarOpen ? "pb-24" : "lg:sticky lg:top-[84px]"}>
              {p.source === "bokun" && <p className="mb-3 text-xl font-bold" data-testid="tour-cached-final-price"><FinalTourPrice id={p.id} unitLabel={priceUnitLabel(p,lang)} /></p>}
              {panel === "maya" && MAYA_PREMIUM_ENABLED ? mayaPanel : renderBookingCard(isDesktop !== true)}
              <GeneralCancellationSummary lang={lang} />
            </div>
          </aside>

        {/* ====================== CONTENIDO ====================== */}
        <div className="mt-2 min-w-0 pb-28 lg:col-start-1 lg:mt-4 lg:pb-14">
          <div className="min-w-0">
            <TourV2Sections tour={p} summary={summary} desc={desc} />

            <div id="v2-guia" className="mt-8 scroll-mt-24">
              <FreeDestinationGuideDownload slug={slug} destinationName={p.location || p.title} />
              <DestinationDiscovery title={p.title} location={p.location} summary={p.summary} itinerary={p.itinerary} highlights={p.highlights} meetingPoints={p.startPoints.map((m) => m.title)} />
              {MAYA_PREMIUM_ENABLED && (
                <div className="mt-8">
                  <MayaAudioGuide
                    title={p.title}
                    location={p.location}
                    summary={summary || (desc ? desc.slice(0, 240) : null)}
                    tourSlug={slug}
                    itinerary={p.itinerary}
                    variant="premium"
                  />
                </div>
              )}
              {p.source === "bokun" && selectorOn && (
                <DirectGuideBenefit tourSlug={slug} className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm font-semibold text-primary" />
              )}
              <WhatsApp24 className="w-fit" />
              {p.source === "bokun" && <CancellationPolicyBlock productId={p.bokunActivityId} />}
              <ReviewsSection slug={slug} source={p.source} tourTitle={p.title} />
              <TourV2Related
                slug={slug}
                location={p.location}
                categories={p.categories ?? []}
                lang={lang}
              />
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Barra móvil del checkout propio: misma selección, mismo total, misma acción */}
      {!selectorOn && (testProductId || liveEnabled) && ownBar && (
        <div
          className="fixed inset-x-0 z-30 px-3 lg:hidden"
          style={{ bottom: "calc(4rem + env(safe-area-inset-bottom, 0px) + 0.5rem)" }}
          data-testid="v2-mobile-bar"
        >
          <div className="mx-auto flex max-w-md items-center justify-between gap-3 rounded-2xl border border-border/70 bg-card/95 px-4 py-2.5 shadow-[0_8px_24px_rgba(23,23,23,0.15)] backdrop-blur">
            <div className="min-w-0">
              <p className="text-[0.65rem] uppercase tracking-[0.1em] text-muted-foreground">
                {ownBar.mode === "total" ? t("tourV2.total", { defaultValue: "Total" }) : ""}
              </p>
              <p className="truncate text-base font-bold">
                {ownBar.amount != null ? `${formatPrice(ownBar.amount, ownBar.currency)} ${ownBar.currency}` : "—"}
              </p>
            </div>
            <button
              type="button"
              disabled={!ownBar.ready}
              onClick={() => ownOpenRef.current?.()}
              className="shrink-0 rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50"
            >
              {ownBar.label}
            </button>
          </div>
        </div>
      )}

      {/* Barra fija móvil, por encima de la navegación inferior */}
      {!selectorOn && !testProductId && !liveEnabled && (
      <div
        className="fixed inset-x-0 z-30 px-3 lg:hidden"
        style={{ bottom: "calc(4rem + env(safe-area-inset-bottom, 0px) + 0.5rem)" }}
      >
        <div className="mx-auto flex max-w-md items-center justify-between gap-3 rounded-2xl border border-border/70 bg-card/95 px-4 py-2.5 shadow-[0_8px_24px_rgba(23,23,23,0.15)] backdrop-blur">
          <div className="min-w-0">
            <p className="text-[0.65rem] uppercase tracking-[0.1em] text-muted-foreground">
              {!officialMode && barStarted ? t("tourV2.total", { defaultValue: "Total" }) : officialMode ? "" : ""}
            </p>
            <p className="whitespace-nowrap text-base font-bold">
              {!officialMode && barStarted ? formatPrice(quote?.total ?? 0, quote?.currency ?? p.currency ?? "MXN") : shownPriceLabel ?? "—"}
              {quote?.rows[0]?.perBooking && (
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  {t("tourV2.perGroup", { defaultValue: "por grupo" })}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => (officialMode || quote?.complete ? openFlow(1) : goToCalendar())}
            className="shrink-0 rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground"
          >
            {officialMode
              ? t("tourV2.pickDate", { defaultValue: "Elegir fecha" })
              : quote?.complete
              ? t("tourV2.bookNowCta", { defaultValue: "Reservar ahora" })
              : !quote?.selection.date
              ? t("tourV2.needDate", { defaultValue: "Selecciona una fecha" })
              : !quote?.selection.startTimeId
              ? t("tourV2.needTime", { defaultValue: "Selecciona un horario" })
              : t("tourV2.needPax", { defaultValue: "Añade viajeros" })}
          </button>
        </div>
      </div>
      )}

      {/* Contenedor único de reservación: selección → datos → pago */}
      {isBokun && p.bokunActivityId && !testProductId && !liveEnabled && !officialMode && (
        <TourV2BookingFlow
          open={flowOpen}
          onOpenChange={setFlowOpen}
          step={flowStep}
          onStepChange={setFlowStep}
          tourTitle={p.title}
          image={p.images[0] ?? null}
          activityId={resolveBookingProductId(slug, "sandbox")}
          ownCheckout={TOUR_V2_OWN_CHECKOUT && !!resolveBookingProductId(slug, "sandbox")}
          notConfiguredMessage={
            TOUR_V2_OWN_CHECKOUT && !resolveBookingProductId(slug, "sandbox") ? NOT_CONFIGURED_IN_TEST : null
          }
          currency={p.currency ?? "MXN"}
          priceFrom={p.priceFrom}
          pricingCategories={p.pricingCategories}
          minBookable={p.minBookable}
          maxBookable={p.maxBookable}
          pickupOptions={p.pickupOptions}
          cancellationPolicy={p.cancellationPolicy}
          selection={selection}
          onSelectionChange={setSelection}
          quote={quote}
          onQuoteChange={setQuote}
        />
      )}
    </div>
  );
}
